import type { AnchorHTMLAttributes } from 'react';

import { IconCircle } from 'components/Icon';
import Img from 'components/Img';
import Link from 'components/Link';
import { ScLink, type ValidSitecoreLink } from 'components/SitecoreLink';
import Text from 'components/Text';
import { cn } from 'utils/classNames';

interface BaseProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
	className?: string;
	hideIconWhenSmall?: boolean;
	imgHeight: number;
	imgSrc: string;
	imgWidth: number;
}

interface RegularLinkItemProps extends BaseProps {
	href: string;
	link?: never;
	text: string;
}

interface SitecoreLinkItemProps extends BaseProps {
	href?: never;
	link: ValidSitecoreLink;
	text?: never;
}

type Props = RegularLinkItemProps | SitecoreLinkItemProps;

interface ImageTextProps {
	height: number;
	hideIconWhenSmall?: boolean;
	src: string;
	text: string;
	width: number;
}
function ImageText({
	height,
	hideIconWhenSmall,
	src,
	text,
	width,
}: ImageTextProps) {
	return (
		<>
			<Img
				src={src}
				// Skip alt text on purpose since the link has a proper text below.
				alt=""
				width={width}
				height={height}
				className="mb-2 w-full"
			/>
			<Text as="span" className="flex items-start">
				<IconCircle
					name="arrow-forward"
					variant="branded"
					className={cn(
						'mr-2 group-hover/shortcut-image:bg-brandHover forced-colors:border',
						hideIconWhenSmall && 'max-sm:hidden',
					)}
				/>
				<span className="group-hover/shortcut-image:underline">{text}</span>
			</Text>
		</>
	);
}
ImageText.displayName = 'BlockShortcutImages_ImageText';

export default function ShortcutImage({
	className,
	hideIconWhenSmall = false,
	href,
	imgHeight,
	imgSrc,
	imgWidth,
	link,
	text,
	...attrs
}: Props) {
	const linkClassName = cn('group/shortcut-image inline-block', className);

	if (link) {
		return (
			<ScLink {...attrs} link={link} className={linkClassName}>
				<ImageText
					src={imgSrc}
					width={imgWidth}
					height={imgHeight}
					hideIconWhenSmall={hideIconWhenSmall}
					text={link.text}
				/>
			</ScLink>
		);
	}

	return (
		<Link {...attrs} href={href} className={linkClassName}>
			<ImageText
				src={imgSrc}
				width={imgWidth}
				height={imgHeight}
				hideIconWhenSmall={hideIconWhenSmall}
				text={text}
			/>
		</Link>
	);
}
ShortcutImage.displayName = 'BlockShortcutImages_ShortcutImage';
